import { useCallback, useEffect } from 'react';
import Raven from 'raven-js';
import { useDispatch } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { markActionComplete } from 'user-context/onboarding';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import MeetingsZeroStatePreview from './MeetingsZeroStatePreview';
import MeetingsUser from 'MeetingsBase/models/MeetingsUser';
import UsageTracker, { trackSubScreen } from '../tracking/UsageTracker';
// @ts-expect-error ts-migrate(7016) file not migrated to TS
import { createMeetingsUser } from '../redux/actions/MeetingsUserActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function MeetingsUserEmptyState({
  hideEmptyState,
  meetingLinkCreated,
  setShowWelcomeModal
}) {
  const dispatch = useDispatch();
  const handleCreateMeetingsUser = useCallback(() => {
    const emptyMeetingsUser = new MeetingsUser();
    dispatch(createMeetingsUser(emptyMeetingsUser)).then(() => {
      UsageTracker.track('activation', {
        step: 'activated'
      });

      // This sets the 'Setup personal meetings link' onboarding step as complete. Chat to #growth-onboarding with questions
      markActionComplete('setup-personal-meetings-link', {
        showTaskCompletionModal: false
      }).catch(error => Raven.captureException(error, {
        extra: {
          message: 'Error when marking `set up personal meeting link` as complete'
        }
      }));
      setShowWelcomeModal();
    }).catch(error => {
      Raven.captureException(error, {
        extra: {
          message: 'Exception occurred while creating an empty meeting user'
        }
      });
    });
  }, [dispatch, setShowWelcomeModal]);
  const handleButtonOnClick = useCallback(() => {
    if (meetingLinkCreated) {
      // Do not create meeting link as it is created in background already
      hideEmptyState();
      setShowWelcomeModal();
    } else {
      handleCreateMeetingsUser();
    }
    window.dispatchEvent(new CustomEvent('HAS_CREATED_MEETING_LINK'));
  }, [handleCreateMeetingsUser, hideEmptyState, meetingLinkCreated, setShowWelcomeModal]);
  const EmptyStateSecondaryContent = () => {
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      alignItems: "center",
      children: [/*#__PURE__*/_jsx(MeetingsZeroStatePreview, {}), /*#__PURE__*/_jsx(Text, {
        as: "small",
        marginBlockStart: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "meetingsUserEmptyState.microCopy"
        })
      })]
    });
  };
  useEffect(() => {
    trackSubScreen('meetings-free-starter-empty-state');
  }, []);
  return /*#__PURE__*/_jsx(EmptyState, {
    maxWidth: 1100,
    titleText: /*#__PURE__*/_jsx(Text, {
      as: "h4",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "meetingsUserEmptyState.title"
      })
    }),
    primaryContent: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      alignItems: "start",
      children: [/*#__PURE__*/_jsxs(List, {
        styled: true,
        marginBlockStart: "300",
        children: [/*#__PURE__*/_jsx(ListItem, {
          marginBlockEnd: "600",
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "meetingsUserEmptyState.description1"
          })
        }), /*#__PURE__*/_jsx(ListItem, {
          marginBlockEnd: "600",
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "meetingsUserEmptyState.description2"
          })
        }), /*#__PURE__*/_jsx(ListItem, {
          marginBlockEnd: "600",
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "meetingsUserEmptyState.description3"
          })
        })]
      }), /*#__PURE__*/_jsx(Button, {
        marginBlockStart: "300",
        use: "primary",
        onClick: handleButtonOnClick,
        "data-selenium-test": "get-started",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "meetingsUserEmptyState.primaryAction"
        })
      })]
    }),
    secondaryContent: /*#__PURE__*/_jsx(EmptyStateSecondaryContent, {}),
    secondaryContentWidth: 150
  });
}